import { useSyncExternalStore } from "react";

/*
 * Emoji the user reacts with most, kept on this device (localStorage), for the
 * one-click quick reactions in the message toolbar and context menu. Scored by
 * "frecency": each use counts, recent uses count more.
 */

const KEY = "atrium:emoji-usage";
const MAX_TRACKED = 40;
const HALF_LIFE_MS = 7 * 86_400_000;
/** Shown until the user has history of their own (and to fill gaps). */
export const DEFAULT_QUICK_REACTIONS = ["\u{1F44D}", "❤️", "\u{1F602}", "\u{1F389}", "\u{1F440}", "\u{1F525}"];

type Usage = Record<string, { n: number; at: number }>;

const listeners = new Set<() => void>();
let cache: Usage | null = null;
let snapshot: { usage: Usage; ranked: string[] } | null = null;

function read(): Usage {
  if (cache) return cache;
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    cache = v && typeof v === "object" && !Array.isArray(v) ? (v as Usage) : {};
  } catch {
    cache = {};
  }
  return cache;
}

function score(u: { n: number; at: number }, now: number) {
  return u.n * Math.pow(0.5, (now - u.at) / HALF_LIFE_MS);
}

function ranked(): string[] {
  const usage = read();
  if (snapshot?.usage === usage) return snapshot.ranked;
  const now = Date.now();
  const list = Object.entries(usage)
    .sort((a, b) => score(b[1], now) - score(a[1], now))
    .map(([e]) => e);
  snapshot = { usage, ranked: list };
  return list;
}

/** Record that the user reacted with (or picked) an emoji. */
export function recordEmojiUse(emoji: string) {
  const usage = { ...read() };
  const prev = usage[emoji];
  usage[emoji] = { n: (prev?.n ?? 0) + 1, at: Date.now() };
  const now = Date.now();
  const keep = Object.entries(usage)
    .sort((a, b) => score(b[1], now) - score(a[1], now))
    .slice(0, MAX_TRACKED);
  cache = Object.fromEntries(keep);
  try {
    localStorage.setItem(KEY, JSON.stringify(cache));
  } catch {
    /* storage unavailable: keep in memory */
  }
  listeners.forEach((l) => l());
}

/** The user's top `count` emoji, topped up with sensible defaults. */
export function quickReactions(list: string[], count: number): string[] {
  const out: string[] = [];
  for (const e of [...list, ...DEFAULT_QUICK_REACTIONS]) {
    if (!out.includes(e)) out.push(e);
    if (out.length === count) break;
  }
  return out;
}

export function useQuickReactions(count: number): string[] {
  const list = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    ranked,
    ranked,
  );
  return quickReactions(list, count);
}
